import { API_URL } from '@/config/env';
import { ApiError } from './types';

/**
 * The one place a request leaves this app.
 *
 * There is no token refresh here, and that is not an omission: a 401 means the
 * token is dead — expired, or its sessions ended by an administrator — and no
 * refresh brings a dead token back. The only correct response is to clear the
 * session and send the reader back to sign in. A wall screen does renew its token,
 * once a day while it is still alive — that is `app/auth.tsx`, not a retry here.
 */

export type QueryValue = string | number | boolean | null | undefined | Array<string | number>;
export type Query = Record<string, QueryValue>;

/**
 * Serialize a query.
 *
 * A blank value is not a filter: `?city=` narrows nothing and some endpoints
 * reject it outright, so empties are dropped here and form state can be passed
 * through untouched. Arrays become one comma-separated parameter, which this
 * backend reads as "any of these".
 */
export const buildQuery = (query: Query): string => {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;

    if (Array.isArray(value)) {
      const items = value.map(String).filter((item) => item.trim() !== '');
      if (items.length > 0) params.set(key, items.join(','));
      continue;
    }

    const text = String(value).trim();
    if (text !== '') params.set(key, text);
  }

  const search = params.toString();
  return search ? `?${search}` : '';
};

/** Set by the auth layer, so this module owns no session state of its own. */
type TokenProvider = () => string | null;
type SessionEndedHandler = () => void;

let getToken: TokenProvider = () => null;
let onSessionEnded: SessionEndedHandler = () => {};

export const configureAuth = (handlers: {
  getToken: TokenProvider;
  /** Called once per 401: clear the session and route to the login screen. */
  onSessionEnded: SessionEndedHandler;
}): void => {
  getToken = handlers.getToken;
  onSessionEnded = handlers.onSessionEnded;
};

/**
 * The endpoints that must not trigger the session-ended handler.
 *
 * On `/login/authenticate` a 401 would mean "these credentials are wrong", not
 * "your session ended" — and bouncing to the login screen from the login screen
 * would wipe the error the reader needs to read.
 */
const AUTH_FREE = ['/login/authenticate', '/register/sign-up'];
const isAuthFree = (path: string): boolean => AUTH_FREE.some((entry) => path.includes(entry));

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { method = 'GET', body, query, signal } = options;

  const url = `${API_URL}${path.startsWith('/') ? path : `/${path}`}${query ? buildQuery(query) : ''}`;
  const token = getToken();

  const response = await fetch(url, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      // Matched literally as `Bearer ` server-side: a lowercase `bearer` or a bare
      // token reads as no credentials at all, which then looks like an expired
      // session rather than a malformed header.
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });

  const text = await response.text();
  const parsed: unknown = text ? safeParse(text) : null;

  if (response.status === 401 && !isAuthFree(path)) {
    // No header, wrong scheme, expired token, or a user blocked since it was
    // issued — the server reports all of them as 401, and none of them can be
    // recovered from without signing in again.
    onSessionEnded();
    throw new ApiError(401, 'Session expired', parsed);
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      messageFrom(parsed) ?? `Request failed (${response.status})`,
      parsed,
    );
  }

  return parsed as T;
};

const safeParse = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    // A proxy error page or a plain-text message: keep it as the body so the
    // caller can still show something true.
    return text;
  }
};

/** This backend puts its error text in `message`; a proxy may send a bare string. */
const messageFrom = (body: unknown): string | null => {
  if (typeof body === 'string' && body.trim() !== '') return body;

  if (body !== null && typeof body === 'object' && 'message' in body) {
    const { message } = body as { message?: unknown };
    if (typeof message === 'string' && message.trim() !== '') return message;
  }

  return null;
};

/**
 * A response that is a file, not JSON.
 *
 * Kept beside `request` rather than folded into it, because the two disagree about
 * the body: `request` always reads text so an error page can be kept and shown,
 * and a spreadsheet read as text is destroyed. The auth header and the 401 rule
 * are the same — a session that ends mid-export ends the same way it ends
 * anywhere else. An ERROR body is still text: this backend reports export
 * failures as JSON or a bare message, never as a file.
 */
const requestBlob = async (path: string, body?: unknown): Promise<Blob> => {
  const url = `${API_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const token = getToken();

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 401 && !isAuthFree(path)) {
    onSessionEnded();
    throw new ApiError(401, 'Session expired', null);
  }

  if (!response.ok) {
    const text = await response.text();
    const parsed: unknown = text ? safeParse(text) : null;
    throw new ApiError(
      response.status,
      messageFrom(parsed) ?? `Request failed (${response.status})`,
      parsed,
    );
  }

  return response.blob();
};

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal): Promise<T> =>
    request<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),

  // A signal matters on POST too: this backend filters through a POST body, so a
  // table whose filters change while a request is in flight needs to abandon it.
  post: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      ...(body === undefined ? {} : { body }),
      ...(signal ? { signal } : {}),
    }),

  put: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', ...(body === undefined ? {} : { body }) }),

  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),

  /** A POST whose answer is a file. See `requestBlob` for why it is separate. */
  download: (path: string, body?: unknown): Promise<Blob> => requestBlob(path, body),
};
